import { assertFocusIdentity, focusIdentitySession, type FocusIdentityGuard } from "./ownership";
import { supabase, supabaseUrl } from "@/integrations/supabase/client";

const LOG = "[focus/completionVoice]";

// Fetches a public URL for the focus session completion voice phrase.
// Returns null on any failure — caller falls back to Tier 1 beep.
export async function fetchCompletionVoiceUrl(
  phraseKey: 1 | 2 | 3 | 4,
  language: string,
  ownerGuard?: FocusIdentityGuard
): Promise<string | null> {
  try {
    assertFocusIdentity(ownerGuard);
    const session = ownerGuard ? await focusIdentitySession(ownerGuard) : (await supabase.auth.getSession()).data.session;
    assertFocusIdentity(ownerGuard);
    if (!session?.access_token) {
      console.warn(LOG + " no active session — skipping voice fetch");
      return null;
    }

    const resp = await fetch(
      supabaseUrl + "/functions/v1/focus-completion-voice",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + session.access_token,
        },
        body: JSON.stringify({ phrase_key: phraseKey, language }),
      }
    );

    assertFocusIdentity(ownerGuard);
    if (!resp.ok) {
      console.error(LOG + " edge fn returned " + resp.status);
      return null;
    }

    assertFocusIdentity(ownerGuard);
    const data: unknown = await resp.json();
    assertFocusIdentity(ownerGuard);
    if (ownerGuard) await focusIdentitySession(ownerGuard);
    assertFocusIdentity(ownerGuard);
    if (
      !data ||
      typeof data !== "object" ||
      typeof (data as Record<string, unknown>).url !== "string"
    ) {
      console.error(LOG + " unexpected response shape:", data);
      return null;
    }

    return (data as { url: string }).url;
  } catch (e) {
    console.error(LOG + " fetch error:", e);
    return null;
  }
}

// iOS audio unlock: must be called in a user gesture context (button click).
// Plays then immediately pauses so the browser permits play() later from
// a non-gesture context (the setInterval timer completion callback).
export async function unlockAudioElement(audio: HTMLAudioElement): Promise<void> {
  try {
    await audio.play();
    audio.pause();
    audio.currentTime = 0;
  } catch {
    // Silently ignore — src may not be set yet or gesture already consumed.
    // The fallback to Tier 1 beep handles the unplayable case.
  }
}
